/**
 * QueryBuilder Component
 *
 * Tab-based builder for SQL queries in ELT jobs.
 * Queries are displayed as horizontal tabs (executed left to right).
 * Landing table configuration is always visible per query.
 * Parameters are managed at the job level, not per query.
 */

import React, { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SqlEditor } from '../../../components/ui/SqlEditor';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select } from '../../../components/ui/Select';
import { FormField } from '../../../components/forms/FormField';
import type {
  EltQueryWithLandingDTO,
  LandingConfigurationCreateDTO,
  SourceColumnDefinition,
  CleanupStrategy
} from '../../../types/elt-job.types';
import { PlusIcon, TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * Extract variables from SQL query using {{variableName}} pattern
 * Exported for use by parent components to detect all variables across queries
 */
export const extractVariablesFromSql = (sql: string): string[] => {
  const regex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
  const matches = new Set<string>();
  let match;
  while ((match = regex.exec(sql)) !== null) {
    matches.add(match[1]);
  }
  return Array.from(matches);
};

interface QueryBuilderProps {
  queries: EltQueryWithLandingDTO[];
  onChange: (queries: EltQueryWithLandingDTO[]) => void;
  errors?: Record<string, string>;
}

export const QueryBuilder: React.FC<QueryBuilderProps> = ({
  queries,
  onChange,
  errors,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const addQuery = () => {
    const newQuery: EltQueryWithLandingDTO = {
      queryName: '',
      sqlQuery: '',
      orderIndex: queries.length,
      isActive: true,
      landingConfig: {
        schemaName: '',
        tableName: '',
        autoCreateTable: true,
        sourceColumns: [],
        cleanupStrategy: { strategy: 'TRUNCATE' },
      },
    };
    onChange([...queries, newQuery]);
    setActiveTab(queries.length);
  };

  const removeQuery = (index: number) => {
    const updatedQueries = queries
      .filter((_, i) => i !== index)
      .map((q, i) => ({ ...q, orderIndex: i }));
    onChange(updatedQueries);
    // Adjust active tab
    if (activeTab >= updatedQueries.length) {
      setActiveTab(Math.max(0, updatedQueries.length - 1));
    } else if (activeTab > index) {
      setActiveTab(activeTab - 1);
    }
  };

  const updateQuery = (index: number, updates: Partial<EltQueryWithLandingDTO>) => {
    const updatedQueries = queries.map((q, i) =>
      i === index ? { ...q, ...updates } : q
    );
    onChange(updatedQueries);
  };

  const updateLandingConfig = (index: number, config: LandingConfigurationCreateDTO | undefined) => {
    updateQuery(index, { landingConfig: config });
  };

  const moveQuery = (index: number, direction: 'left' | 'right') => {
    if (
      (direction === 'left' && index === 0) ||
      (direction === 'right' && index === queries.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'left' ? index - 1 : index + 1;
    const updatedQueries = [...queries];
    [updatedQueries[index], updatedQueries[newIndex]] = [
      updatedQueries[newIndex],
      updatedQueries[index],
    ];
    // Update orderIndex and follow the moved tab
    onChange(updatedQueries.map((q, i) => ({ ...q, orderIndex: i })));
    setActiveTab(newIndex);
  };

  const activeQuery = queries[activeTab];

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <div className="flex-1 flex items-center overflow-x-auto">
          {queries.map((query, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActiveTab(index)}
              className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === index
                  ? 'text-primary-600 border-primary-600 bg-primary-50/50'
                  : query.isActive === false
                  ? 'text-gray-400 border-transparent hover:text-gray-500 hover:border-gray-300'
                  : 'text-gray-600 border-transparent hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
                {index + 1}
              </span>
              <span className="max-w-[120px] truncate">
                {query.queryName || `Query ${index + 1}`}
              </span>
              {query.isActive === false && (
                <span className="text-xs text-gray-400">(off)</span>
              )}
            </button>
          ))}
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={addQuery} className="shrink-0">
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Query
        </Button>
      </div>

      {/* Empty state */}
      {queries.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500">No queries defined. Add at least one query.</p>
          <Button type="button" variant="primary" size="sm" className="mt-3" onClick={addQuery}>
            Add First Query
          </Button>
        </div>
      )}

      {/* Active query content */}
      {activeQuery && (
        <div className="space-y-6">
          {/* Query toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => moveQuery(activeTab, 'left')}
                disabled={activeTab === 0}
                title="Move query left (executes earlier)"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => moveQuery(activeTab, 'right')}
                disabled={activeTab === queries.length - 1}
                title="Move query right (executes later)"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-500">
                Execution order: {activeTab + 1} of {queries.length}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={activeQuery.isActive !== false}
                  onChange={(e) => updateQuery(activeTab, { isActive: e.target.checked })}
                />
                Active
              </label>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => removeQuery(activeTab)}
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>

          {/* Query Name */}
          <FormField
            label="Query Name"
            required
            hint="Used as the output filename"
            error={errors?.[`queries.${activeTab}.queryName`]}
          >
            <Input
              value={activeQuery.queryName}
              onChange={(e) => updateQuery(activeTab, { queryName: e.target.value })}
              placeholder="users_export"
              error={!!errors?.[`queries.${activeTab}.queryName`]}
            />
          </FormField>

          {/* SQL Query */}
          <FormField
            label="SQL Query"
            required
            hint="Use {{parameterName}} for dynamic values. Multiple statements can be separated by semicolons."
            error={errors?.[`queries.${activeTab}.sqlQuery`]}
          >
            <SqlEditor
              value={activeQuery.sqlQuery}
              onChange={(value) => updateQuery(activeTab, { sqlQuery: value })}
              placeholder="SELECT * FROM users WHERE created_at >= '{{start_date}}'"
              minHeight="160px"
              maxHeight="400px"
            />
          </FormField>

          {/* Detected Variables Info */}
          {(() => {
            const detectedVariables = extractVariablesFromSql(activeQuery.sqlQuery);
            return detectedVariables.length > 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Detected parameters: </span>
                  {detectedVariables.map((v, i) => (
                    <span key={v}>
                      <code className="bg-blue-100 px-1 rounded">{`{{${v}}}`}</code>
                      {i < detectedVariables.length - 1 && ', '}
                    </span>
                  ))}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Set default values in the Parameters section.
                </p>
              </div>
            ) : null;
          })()}

          {/* Landing Table Configuration - Always visible */}
          <LandingConfigEditor
            config={activeQuery.landingConfig!}
            onChange={(config) => updateLandingConfig(activeTab, config)}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Landing Configuration Editor Component
 */
interface LandingConfigEditorProps {
  config: LandingConfigurationCreateDTO;
  onChange: (config: LandingConfigurationCreateDTO) => void;
}

const LandingConfigEditor: React.FC<LandingConfigEditorProps> = ({
  config,
  onChange,
}) => {
  const updateConfig = (updates: Partial<LandingConfigurationCreateDTO>) => {
    onChange({ ...config, ...updates });
  };

  const addColumn = () => {
    updateConfig({
      sourceColumns: [...config.sourceColumns, { sourceColumn: '' }],
    });
  };

  const updateColumn = (index: number, updates: Partial<SourceColumnDefinition>) => {
    const columns = [...config.sourceColumns];
    columns[index] = { ...columns[index], ...updates };
    updateConfig({ sourceColumns: columns });
  };

  const removeColumn = (index: number) => {
    updateConfig({
      sourceColumns: config.sourceColumns.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-4">
      <h4 className="text-sm font-semibold text-gray-900">Landing Table Configuration</h4>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Target Schema Name" hint="Schema for data landing (e.g., source database name). Supports {{parameter}} placeholders.">
          <Input
            value={config.schemaName || ''}
            onChange={(e) => updateConfig({ schemaName: e.target.value || undefined })}
            placeholder="softland"
          />
        </FormField>

        <FormField label="Target Table Name" required hint="Table name within the schema. Supports {{parameter}} placeholders.">
          <Input
            value={config.tableName}
            onChange={(e) => updateConfig({ tableName: e.target.value })}
            placeholder="mov_contables"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={config.autoCreateTable !== false}
              onChange={(e) => updateConfig({ autoCreateTable: e.target.checked })}
            />
            Auto-create table if not exists
          </label>
        </div>

        <FormField label="Cleanup Strategy" hint="How to handle existing data before loading">
          <Select
            value={config.cleanupStrategy?.strategy || 'NONE'}
            onChange={(e) => updateConfig({
              cleanupStrategy: {
                ...config.cleanupStrategy,
                strategy: e.target.value as CleanupStrategy
              }
            })}
          >
            <option value="NONE">None - Append data</option>
            <option value="TRUNCATE">Truncate - Clear table before load</option>
            <option value="DELETE_BY_PARAMS">Delete by Parameters - Conditional delete</option>
            <option value="DROP_AND_CREATE">Drop & Recreate - Replace table schema</option>
          </Select>
        </FormField>
      </div>

      {config.cleanupStrategy?.strategy === 'DELETE_BY_PARAMS' && (
        <FormField
          label="Delete Condition"
          hint="SQL WHERE clause with {{parameter}} placeholders"
        >
          <Input
            value={config.cleanupStrategy.deleteCondition || ''}
            onChange={(e) => updateConfig({
              cleanupStrategy: {
                ...config.cleanupStrategy!,
                deleteCondition: e.target.value
              }
            })}
            placeholder="year = {{year}} AND month = {{month}}"
          />
        </FormField>
      )}

      {/* Source Columns */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Source Columns</label>
          <Button type="button" variant="secondary" size="sm" onClick={addColumn}>
            <PlusIcon className="h-3 w-3 mr-1" />
            Add Column
          </Button>
        </div>

        {config.sourceColumns.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No columns defined. All columns from query result will be used.
          </p>
        ) : (
          <div className="space-y-2">
            {config.sourceColumns.map((col, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input
                  value={col.sourceColumn}
                  onChange={(e) => updateColumn(idx, { sourceColumn: e.target.value })}
                  placeholder="Source column name"
                  className="flex-1"
                />
                <Input
                  value={col.targetColumn || ''}
                  onChange={(e) => updateColumn(idx, { targetColumn: e.target.value || undefined })}
                  placeholder="Target column (optional)"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => removeColumn(idx)}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
